import type { OnSelectCallbackParams } from '@/components/StructuredVirtualList'

import { useCallback } from 'react'

import { useReadOnly } from '@/hooks'
import { fitMapToSelection } from '@/map'

import { useDataForList } from './useDataForList'
import { useCanEditGpsData } from './useCanEditGpsData'

export function useListHandlers(selection: workwave.DeepReadonly<string[]>) {
  const { readonly } = useReadOnly()
  const { structure, setEditing, deselectAll, setSelection } = useDataForList()
  const canEditGpsData = useCanEditGpsData()

  const handleOnSelect = useCallback(
    (params: OnSelectCallbackParams<'unifiedVehicles'>) => {
      const vehicleIds: string[] = []

      for (const vehicleId of params.newSelectedIds) {
        if (vehicleId.startsWith('group')) continue

        vehicleIds.push(vehicleId)
      }

      setSelection(vehicleIds)

      // try to select it right away and
      // queue a pending fitMap request
      // it will be applied by the destination view when it opens
      fitMapToSelection({ preventIfVisible: true }, 'add')
    },
    [setSelection],
  )

  const handleOnSelectAll = useCallback(() => {
    const allSelected = structure.shownItems <= selection.length

    const vehicleIds: string[] = []

    for (const listItem of structure.list) {
      if (typeof listItem === 'string') {
        vehicleIds.push(listItem)
      }
    }

    if (allSelected) {
      deselectAll()
    } else {
      setSelection(vehicleIds)

      // try to select it right away and
      // queue a pending fitMap request
      // it will be applied by the destination view when it opens
      fitMapToSelection({ preventIfVisible: true }, 'add')
    }
  }, [setSelection, selection, deselectAll, structure])

  const handleOnDoubleClick = useCallback(() => {
    if (!canEditGpsData || readonly) return

    setEditing([...selection])
  }, [setEditing, readonly, selection, canEditGpsData])

  return {
    handleOnSelect,
    handleOnSelectAll,
    handleOnDoubleClick,
  }
}
